<template>
  <v-dialog
    v-model="show"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    max-width="820"
    scrollable
    persistent
    transition="dialog-bottom-transition"
  >
    <v-card class="ba-card">
      <v-card-title class="ba-header">
        <v-icon color="white" small class="mr-2">mdi-clipboard-account-outline</v-icon>
        <span class="ba-title">Anamnese na consulta</span>
        <span v-if="patientName" class="ba-patient">· {{ patientName }}</span>
        <v-spacer />
        <v-chip v-if="anamneseId" x-small color="white" text-color="primary" class="mr-2">
          {{ respondidas }} respondida(s)
        </v-chip>
        <v-btn class="close-btn" icon color="white" @click="fechar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- ETAPA 1 — escolher o roteiro -->
      <v-card-text v-if="!anamneseId" class="ds-dialog-body">
        <div v-if="carregandoTemplates" class="ba-loading">
          <v-progress-circular indeterminate color="primary" size="28" />
          <span class="ml-3">Carregando os roteiros…</span>
        </div>

        <section v-else class="ds-form-section">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-clipboard-list</v-icon>
              <span>Roteiro</span>
            </div>
          </div>
          <p class="ba-lead">
            As respostas vão para o prontuário nas mesmas tabelas das que chegam
            pelo WhatsApp — a diferença é que ficam marcadas como registradas por
            você, na consulta.
          </p>

          <v-select
            v-model="templateId"
            :items="templates"
            item-text="name"
            item-value="id"
            label="Roteiro de perguntas"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
          />

          <v-btn
            color="primary"
            depressed
            :disabled="!templateId"
            :loading="abrindo"
            @click="iniciar"
          >
            <v-icon left small>mdi-play</v-icon>
            Começar
          </v-btn>
        </section>
      </v-card-text>

      <!-- ETAPA 2 — a conversa -->
      <v-card-text v-else ref="conversa" class="ds-dialog-body ba-body--conversa">
        <section class="ds-form-section">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-forum</v-icon>
              <span>Conversa</span>
            </div>
          </div>
          <div v-for="(turno, i) in conversa" :key="i" class="ba-turno">
            <div class="ba-linha ba-linha--esq">
              <div class="ba-bolha ba-bolha--pergunta">{{ turno.pergunta }}</div>
            </div>
            <div class="ba-linha ba-linha--dir">
              <div
                v-if="turno.resposta"
                class="ba-bolha ba-bolha--resposta"
                :class="{ 'ba-bolha--erro': turno.erro }"
              >
                {{ turno.resposta }}
                <v-icon v-if="turno.erro" x-small color="error" class="ml-1">
                  mdi-alert-circle-outline
                </v-icon>
              </div>
              <div v-else class="ba-bolha ba-bolha--pulada">{{ turno.motivo }}</div>
            </div>
          </div>

          <!-- pergunta corrente -->
          <div v-if="perguntaAtual" class="ba-turno">
            <div class="ba-linha ba-linha--esq">
              <div class="ba-bolha ba-bolha--pergunta ba-bolha--ativa">
                {{ perguntaAtual.prompt }}
              </div>
            </div>
          </div>

          <div v-if="terminou" class="ba-fim">
            <v-icon color="success" small class="mr-1">mdi-check-circle-outline</v-icon>
            Anamnese registrada — {{ respondidas }} resposta(s).
          </div>
        </section>
      </v-card-text>

      <!-- controles de resposta, por tipo de pergunta -->
      <v-card-actions v-if="anamneseId && perguntaAtual" class="ds-dialog-actions">
        <div class="ba-acoes-inner">
          <div v-if="bloqueada" class="ba-bloqueio">
            <v-icon small color="grey darken-1" class="mr-1">mdi-information-outline</v-icon>
            <span>{{ motivo }}</span>
            <v-spacer />
            <v-btn text small @click="pular">Pular</v-btn>
          </div>

          <template v-else>
            <!-- sim/não -->
            <div v-if="tipo === 'sim_nao'" class="ba-opcoes">
              <v-btn small outlined color="primary" :loading="salvando" @click="responder('Sim')">Sim</v-btn>
              <v-btn small outlined color="primary" :loading="salvando" @click="responder('Não')">Não</v-btn>
            </div>

            <!-- escolha única -->
            <div v-else-if="tipo === 'unica'" class="ba-opcoes">
              <v-chip
                v-for="op in opcoes"
                :key="op.label"
                small
                outlined
                :color="rascunho === op.label ? 'primary' : undefined"
                @click="rascunho = op.label"
              >
                {{ op.label }}
              </v-chip>
            </div>

            <!-- escolha múltipla -->
            <div v-else-if="tipo === 'multipla'" class="ba-opcoes">
              <v-chip
                v-for="op in opcoes"
                :key="op.label"
                small
                :outlined="!selecionadas.includes(op.label)"
                :color="selecionadas.includes(op.label) ? 'primary' : undefined"
                @click="alternar(op.label)"
              >
                {{ op.label }}
              </v-chip>
            </div>

            <!-- número: também quando a pergunta declara coluna tipada
                 (altura/peso/dias/minutos). Pedir o valor JÁ na unidade certa
                 evita reimplementar aqui o parser de texto da comunicação. -->
            <v-text-field
              v-else-if="tipo === 'numero' || unidade"
              v-model="rascunho"
              type="number"
              outlined
              dense
              hide-details
              :label="unidade ? `Resposta (${unidade})` : 'Resposta'"
              :suffix="unidade || undefined"
              class="ba-campo"
            />

            <!-- texto -->
            <v-textarea
              v-else
              v-model="rascunho"
              outlined
              dense
              hide-details
              rows="2"
              auto-grow
              label="Resposta"
              class="ba-campo"
            />

            <div class="ba-barra">
              <!--
                salvia-865: o ditado fica SO no texto livre.

                *Sim/nao:* a resposta sai por dois botoes que chamam `responder()`
                direto; o ditado escreve no `rascunho`, que esse tipo nem usa.

                *Numero:* o campo e `type="number"` e o `aoDitar` CONCATENA
                (`rascunho + ' ' + falado`). Ditar duas vezes produz "82 85", que
                nao e numero valido — o campo rejeita e o valor some em silencio.
                Numero errado no prontuario PARECE preciso, e sao tres toques no
                teclado: ganho baixo, modo de falha ruim.

                *Escolha unica e multipla:* aqui havia um casamento desenhado — o
                `aoDitar` comparava o falado com o rotulo da opcao e marcava. Ele
                nunca disparou, e a investigacao de 14/09 mostrou que nao era
                intermitencia: o `mesmoTexto` exige IGUALDADE EXATA, e os rotulos
                reais sao frases ("Nenhum Sintoma", "Voce faz uso de algum
                medicamento de uso continuo?"). O medico fala "nenhum"; o rotulo e
                "nenhum sintoma"; nao casa. Estruturalmente impossivel, nao raro.

                Nao afrouxamos o casamento de proposito: fazer isso e deixar o
                reconhecimento ESCOLHER opcao clinica por aproximacao, e a distancia
                entre "dor de cabeca" e "dor de cabeca intensa" e um fato clinico
                diferente no prontuario. A docstring do `aoDitar` ja marcava essa
                linha — "o reconhecimento nao escolhe opcao clinica sozinho". E a
                opcao esta na tela: tocar o chip e um toque.
              -->
              <DictationButton
                v-if="aceitaDitado"
                hint="Ouvindo a paciente… o texto entra no campo para você conferir"
                empty-text="Fale o que a paciente respondeu…"
                :numbered="false"
                @transcript="aoDitar"
              />
              <span v-if="tipo === 'multipla'" class="ba-dica">
                {{ selecionadas.length }} selecionada(s)
              </span>
              <v-spacer />
              <v-btn text small :disabled="salvando" @click="pular">Pular</v-btn>
              <v-btn
                color="primary"
                depressed
                small
                :disabled="!temResposta"
                :loading="salvando"
                @click="confirmar"
              >
                Salvar
              </v-btn>
            </div>
          </template>
        </div>
      </v-card-actions>

      <v-card-actions v-else-if="terminou" class="ds-dialog-actions">
        <v-spacer />
        <v-btn color="primary" depressed small @click="fechar">Concluir</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Anamnese PRESENCIAL — SALVIA-853.
 *
 * Formato de conversa, de propósito: as perguntas aparecem uma a uma e o médico
 * registra a resposta da paciente, que está na frente dele. É a mesma sequência
 * que a paciente veria no WhatsApp, conduzida por quem está na sala.
 *
 * Duas coisas que esta tela NÃO faz, e não fazer é o ponto:
 *  - não decide o destino da resposta: cada pergunta já traz o seu `fieldKey`,
 *    detectado no save do template (`field_key_detection`);
 *  - não interpreta a resposta: o texto/opção vai como está, e quem grava é o
 *    mesmo `ClinicalAnswerRouter` do webhook do WhatsApp. Sem parser próprio,
 *    sem tabela própria, sem segunda fonte de verdade.
 *
 * O ditado PRÉ-PREENCHE o campo em vez de enviar: o reconhecimento erra, e aqui
 * o texto vira dado clínico. O médico confere e clica em Registrar. (Nota: a
 * SALVIA-849 decidiu o contrário para o chat do assistente — lá a fala vira
 * mensagem direto. A diferença é o que está em jogo em cada tela.)
 */
import DictationButton from "@/features/patients/prescriptions/components/DictationButton.vue";
import { listAnamneseTemplates } from "@/services/anamneseTemplateService";
import {
  abrirAnamnesePresencial,
  salvarResposta,
} from "@/services/bedsideAnamneseService";
import {
  motivoBloqueio,
  paraEnviar,
  podeResponder,
} from "./bedsideFieldShape";

export default {
  name: "BedsideAnamneseDialog",

  components: { DictationButton },

  props: {
    value: { type: Boolean, default: false },
    patientId: { type: [Number, String], required: true },
    patientName: { type: String, default: "" },
  },

  data() {
    return {
      templates: [],
      templateId: null,
      carregandoTemplates: false,
      abrindo: false,
      salvando: false,
      anamneseId: null,
      perguntas: [],
      entryState: null,
      estadoAtual: null,
      visitados: [],
      conversa: [],
      rascunho: "",
      selecionadas: [],
    };
  },

  computed: {
    show: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },

    perguntasPorChave() {
      return this.perguntas.reduce((m, q) => ({ ...m, [q.uid]: q }), {});
    },

    perguntaAtual() {
      return this.estadoAtual ? this.perguntasPorChave[this.estadoAtual] || null : null;
    },

    tipo() {
      return this.perguntaAtual?.answerType || "texto";
    },

    /**
     * salvia-865: ditar so no texto livre. Lista explicita dos tipos que NAO
     * aceitam (e nao "so o texto aceita") para que um tipo NOVO nasca com ditado e
     * alguem decida de proposito — em vez de sumir em silencio por nao estar numa
     * lista de permissao.
     */
    aceitaDitado() {
      return !["sim_nao", "numero", "unica", "multipla"].includes(this.tipo) && !this.unidade;
    },

    opcoes() {
      return this.perguntaAtual?.options || [];
    },

    /** Unidade da coluna tipada da pergunta (cm, kg, dias, minutos), se houver. */
    unidade() {
      return this.perguntaAtual?.typed?.unit || null;
    },

    bloqueada() {
      return !podeResponder(this.perguntaAtual?.fieldKey);
    },

    motivo() {
      return motivoBloqueio(this.perguntaAtual?.fieldKey);
    },

    temResposta() {
      if (this.tipo === "multipla") return this.selecionadas.length > 0;
      return String(this.rascunho || "").trim().length > 0;
    },

    terminou() {
      return Boolean(this.anamneseId) && !this.perguntaAtual;
    },

    respondidas() {
      return this.conversa.filter((t) => t.resposta && !t.erro).length;
    },
  },

  watch: {
    value(aberto) {
      if (aberto && !this.anamneseId) this.carregarTemplates();
    },
  },

  methods: {
    async carregarTemplates() {
      this.carregandoTemplates = true;
      try {
        this.templates = await listAnamneseTemplates();
        const ativo = this.templates.find((t) => t.active) || this.templates[0];
        this.templateId = ativo?.id || null;
      } catch (e) {
        this.$emit("erro", "Não foi possível carregar os roteiros de anamnese.");
      } finally {
        this.carregandoTemplates = false;
      }
    },

    async iniciar() {
      const template = this.templates.find((t) => t.id === this.templateId);
      if (!template) return;

      this.abrindo = true;
      try {
        const anamnese = await abrirAnamnesePresencial(
          this.patientId,
          this.templateId,
        );
        this.anamneseId = anamnese.id;
        this.perguntas = template.questions || [];
        // O fluxo comeca pelo entryState, que NEM SEMPRE e a primeira por ordem.
        this.entryState =
          template.entryState || (this.perguntas[0] && this.perguntas[0].uid) || null;
        this.estadoAtual = this.entryState;
        this.visitados = [];
        this.conversa = [];
      } catch (e) {
        this.$emit("erro", "Não foi possível abrir a anamnese.");
      } finally {
        this.abrindo = false;
      }
    },

    /**
     * O ditado entra no CAMPO, não no envio — e só existe em texto livre.
     *
     * salvia-865: havia aqui um ramo que, em pergunta de opções, casava o falado
     * com o rótulo e marcava a opção. Foi removido junto com o botão nesses tipos:
     * o casamento exigia igualdade exata e nunca disparou (ver o comentário no
     * template). Afrouxá-lo seria deixar o reconhecimento escolher opção clínica
     * por aproximação, que é a linha que o código original não quis cruzar.
     */
    aoDitar(texto) {
      const falado = String(texto || "").trim();
      if (!falado) return;

      this.rascunho = this.rascunho ? `${this.rascunho} ${falado}` : falado;
    },

    /**
     * Marca/desmarca uma opção, respeitando as EXCLUSIVAS da definição.
     *
     * "Nenhum Sintoma" junto com "Outro(s)" é contradição, e a tela aceitava —
     * visto na validação de 10/09. O `exclusive_options` já existia na definição
     * e era descartado na conversão para o editor.
     */
    alternar(label) {
      const exclusivas = this.perguntaAtual?.exclusiveOptions || [];
      const i = this.selecionadas.indexOf(label);

      if (i >= 0) {
        this.selecionadas.splice(i, 1);
        return;
      }

      if (exclusivas.includes(label)) {
        this.selecionadas = [label];   // exclusiva limpa o resto
        return;
      }

      // Escolher qualquer outra derruba a exclusiva que estivesse marcada.
      this.selecionadas = this.selecionadas.filter((x) => !exclusivas.includes(x));
      this.selecionadas.push(label);
    },

    responder(valor) {
      this.rascunho = valor;
      this.confirmar();
    },

    async confirmar() {
      if (!this.temResposta || this.salvando) return;

      const pergunta = this.perguntaAtual;
      const respostas =
        this.tipo === "multipla"
          ? this.selecionadas.slice()
          : [String(this.rascunho).trim()];

      this.salvando = true;
      let erro = false;

      try {
        for (const envio of paraEnviar(pergunta, respostas)) {
          await salvarResposta(this.anamneseId, envio.fieldKey, envio.corpo);
        }
      } catch (e) {
        erro = true;
        // 422 aqui quase sempre significa `field_key` que o router não conhece —
        // foi o que aconteceu em 10/09, com o vocabulário antigo preso no banco.
        // Mensagem genérica esconde isso e manda o médico adivinhar.
        const detalhe =
          e?.response?.status === 422
            ? ` O destino desta pergunta (${pergunta.fieldKey}) não é aceito pelo prontuário.`
            : "";
        this.$emit("erro", `Não foi possível registrar esta resposta.${detalhe}`);
      } finally {
        this.salvando = false;
      }

      this.conversa.push({
        pergunta: pergunta.prompt,
        resposta: respostas.join(", "),
        erro,
      });

      // Erro não avança: a pergunta continua na tela para tentar de novo.
      if (!erro) this.avancar(respostas);
    },

    pular() {
      this.conversa.push({
        pergunta: this.perguntaAtual.prompt,
        resposta: null,
        motivo: this.bloqueada ? this.motivo : "Pulada",
      });
      this.avancar([]);
    },

    /**
     * Decide o proximo estado pelas TRANSICOES da pergunta — a mesma definicao
     * que o fluxo do WhatsApp segue.
     *
     * Caminhar por `ordering` estava errado e apareceu na validacao de 10/09: a
     * anamnese geral RAMIFICA (objectives --opcao "Outro"--> otherObjectives;
     * diseases --opcao "Nenhuma"--> drugs, pulando disease_specific). Sem seguir
     * o grafo, a tela fazia a pergunta que o fluxo pularia — e como alguns
     * follow-ups tem o prompt copiado da pergunta-mae, parecia que tinha voltado
     * para a primeira.
     *
     * @param {string[]} respostas o que foi registrado (labels das opcoes ou texto)
     */
    avancar(respostas = []) {
      const atual = this.perguntaAtual;
      this.rascunho = "";
      this.selecionadas = [];

      const proximo = this.proximoEstado(atual, respostas);

      // Trava de ciclo: definicao mal formada nao pode prender o medico num loop.
      if (proximo && this.visitados.includes(proximo)) {
        this.estadoAtual = null;
      } else {
        if (proximo) this.visitados.push(proximo);
        this.estadoAtual = proximo;
      }

      this.$nextTick(() => {
        const el = this.$refs.conversa?.$el || this.$refs.conversa;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    /** @returns {string|null} chave da proxima pergunta, ou null quando acabou. */
    proximoEstado(pergunta, respostas) {
      if (!pergunta) return null;

      const transicoes = pergunta.transitions || [];
      const destino = (gatilho) =>
        (transicoes.find((t) => t.trigger === gatilho) || {}).to;

      // 1. Opcao com gatilho proprio manda (ex.: "Nenhuma" -> noneDisease, que
      //    pula o "quais delas?"). Se varias forem marcadas, vale a primeira.
      const comGatilho = (pergunta.options || []).find(
        (op) => op.conditionalTrigger && respostas.includes(op.label),
      );
      if (comGatilho) {
        const alvo = destino(comGatilho.conditionalTrigger);
        if (alvo) return alvo === "finished" ? null : alvo;
      }

      // 2. sim/nao: o proprio texto da resposta e o gatilho.
      if (pergunta.answerType === "sim_nao" && respostas.length) {
        const alvo = destino(respostas[0]);
        if (alvo) return alvo === "finished" ? null : alvo;
      }

      // 3. Caminho comum.
      const alvo = destino("text") || (transicoes[0] || {}).to;
      if (!alvo || alvo === "finished") return null;

      return alvo;
    },

    fechar() {
      this.show = false;
      this.$emit("concluida", {
        anamneseId: this.anamneseId,
        respondidas: this.respondidas,
      });
      this.anamneseId = null;
      this.perguntas = [];
      this.conversa = [];
      this.estadoAtual = null;
      this.entryState = null;
      this.visitados = [];
      this.rascunho = "";
      this.selecionadas = [];
    },
  },
};
</script>

<style scoped>
.ba-header {
  background: var(--v-primary-base, #2e7d6f);
  color: #fff;
  padding: 10px 14px;
  font-size: 15px;
}
.ba-title {
  font-weight: 600;
}
.ba-patient {
  opacity: 0.85;
  margin-left: 6px;
  font-weight: 400;
}
/* Sem max-height: o diálogo `scrollable` já limita a 90% da tela e rola só
   o corpo. O min-height evita o diálogo "pular" de tamanho no começo. */
.ba-body--conversa {
  min-height: 320px;
}
.ba-loading {
  display: flex;
  align-items: center;
  padding: 24px 0;
}
.ba-lead {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.66);
  line-height: 1.5;
}
/* Cada turno e uma coluna; pergunta a esquerda, resposta a direita. Antes as
   bolhas usavam `float`, e o float embaralhava a ordem visual: a resposta subia
   para o lado da pergunta seguinte. */
.ba-turno {
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;
}
.ba-linha {
  display: flex;
  margin-top: 4px;
}
.ba-linha--esq {
  justify-content: flex-start;
}
.ba-linha--dir {
  justify-content: flex-end;
}
.ba-bolha {
  max-width: 82%;
  padding: 9px 13px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.45;
}
.ba-bolha--pergunta {
  background: #eceff1;
  border-bottom-left-radius: 3px;
}
.ba-bolha--ativa {
  background: #e3f2fd;
  font-weight: 500;
}
.ba-bolha--resposta {
  background: var(--v-primary-base, #2e7d6f);
  color: #fff;
  border-bottom-right-radius: 3px;
}
.ba-bolha--erro {
  background: #ffcdd2;
  color: rgba(0, 0, 0, 0.8);
}
.ba-bolha--pulada {
  background: #f5f5f5;
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
  font-style: italic;
}
.ba-fim {
  padding-top: 12px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
}
.ba-acoes-inner {
  width: 100%;
}
.ba-opcoes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.ba-campo {
  margin-bottom: 10px;
}
.ba-barra {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ba-dica {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}
.ba-bloqueio {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
}
</style>
